import React, { useEffect, useRef } from 'react';

type StartupSoundProps = {
  enabled?: boolean;
  src?: string;
  volume?: number; // 0.0 - 1.0
};

const STORAGE_KEY = 'MapleOS-startup-sound-played';

const StartupSound: React.FC<StartupSoundProps> = ({ enabled = false, src = '/os/assets/audio/startup.mp3', volume = 0.8 }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const triedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    // Prevent multiple plays in the same session (use sessionStorage)
    const alreadyPlayed = sessionStorage.getItem(STORAGE_KEY) === 'true';
    if (alreadyPlayed || triedRef.current) return;

    triedRef.current = true;

    // Create and configure audio
    const audio = new Audio(src);
    audioRef.current = audio;
    audio.preload = 'auto';
    audio.volume = Math.min(1, Math.max(0, volume));

    const cleanup = () => {
      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };

    // Try playing after several possible user gestures
    const gestureEvents = ['click', 'keydown', 'pointerdown', 'touchstart', 'mousemove'] as const;
    const removeGestureListeners = () => {
      gestureEvents.forEach((evt) => document.removeEventListener(evt, playAfterGesture, true));
    };
    const playAfterGesture = () => {
      audio.play().then(() => {
        sessionStorage.setItem(STORAGE_KEY, 'true');
        removeGestureListeners();
      }).catch(() => {
        // Keep listeners if it still fails
      });
    };

    // Try to play immediately
    audio.play().then(() => {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    }).catch(() => {
      // Autoplay can be blocked; wait for any user gesture
      gestureEvents.forEach((evt) => document.addEventListener(evt, playAfterGesture, { capture: true, once: false }));
    });

    // Cleanup when unmounting
    return () => {
      removeGestureListeners();
      cleanup();
    };
  }, [enabled, src, volume]);

  return null;
};

export default StartupSound;